import React, { useEffect, useState } from 'react';
import { MultiSelect } from '../components/MultiSelect';
import { FieldsTable } from '../components/FieldsTable';
import { EditionSelector } from '../components/EditionSelector';
import { PlanSelector } from '../components/PlanSelector';
import { SmartAskAmountsInfo } from '../components/SmartAskAmountsInfo';
import { getUniqueProducts, getUniqueFeatures, getFilteredFields, getFieldsByProduct } from '../data/fieldRequirements';
import { getEditionByName } from '../data/editionData';
import { getPlanByName } from '../data/planData';
import { getProductOptions } from '../data/productMapping';
type FilterMode = 'product-feature' | 'edition' | 'plan' | 'vm';
export function FieldRequirementsTool() {
  const [filterMode, setFilterMode] = useState<FilterMode>('product-feature');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedEdition, setSelectedEdition] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [includeGO, setIncludeGO] = useState(false);
  const [includeVM, setIncludeVM] = useState(false);
  const [includeEvents, setIncludeEvents] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState<string[]>([]);
  // Get product options with grouping
  const productOptions = getProductOptions();
  // Update available features when products change
  useEffect(() => {
    if (filterMode === 'product-feature') {
      const features = getUniqueFeatures(selectedProducts);
      setAvailableFeatures(features);
      // Auto-select all features for products that have data-powered features
      const productsWithAutoFeatures = ['Outreach', 'Events', 'Online Giving'];
      const selectedProductsWithFeatures = selectedProducts.filter(p => productsWithAutoFeatures.includes(p));
      if (selectedProductsWithFeatures.length > 0) {
        const autoSelectedFeatures = features.filter(feature => {
          return selectedProductsWithFeatures.some(product => {
            const productFeatures = getUniqueFeatures([product]);
            return productFeatures.includes(feature);
          });
        });
        const allSelectedFeatures = Array.from(new Set([...selectedFeatures, ...autoSelectedFeatures]));
        if (JSON.stringify(allSelectedFeatures.sort()) !== JSON.stringify(selectedFeatures.sort())) {
          setSelectedFeatures(allSelectedFeatures);
        }
      } else {
        setSelectedFeatures(prev => prev.filter(feature => features.includes(feature)));
      }
    }
  }, [selectedProducts, filterMode]);
  // Handle edition selection and GO/VM toggles
  useEffect(() => {
    if (filterMode === 'edition' && selectedEdition) {
      const edition = getEditionByName(selectedEdition);
      if (edition) {
        const editionProducts = edition.productFeatures.map(pf => pf.product);
        const editionFeatures = edition.productFeatures.flatMap(pf => pf.features);
        const products = [...editionProducts];
        const features = [...editionFeatures];
        if (includeGO && !products.includes('GO')) {
          products.push('GO');
          features.push('GO');
        }
        if (includeVM && !products.includes('VM')) {
          products.push('VM');
          features.push('Core');
        }
        setSelectedProducts(products);
        setSelectedFeatures(features);
      }
    }
  }, [selectedEdition, includeGO, includeVM, filterMode]);
  // Handle plan selection and add-on toggles
  useEffect(() => {
    if (filterMode === 'plan' && selectedPlan) {
      const plan = getPlanByName(selectedPlan);
      if (plan) {
        const planProducts = plan.productFeatures.map(pf => pf.product);
        const planFeatures = plan.productFeatures.flatMap(pf => pf.features);
        const products = [...planProducts];
        const features = [...planFeatures];
        if (includeEvents && !products.includes('Events')) {
          products.push('Events');
          features.push('Constituent ID Matching');
        }
        if (includeGO && !products.includes('GO')) {
          products.push('GO');
          features.push('GO');
        }
        if (includeVM && !products.includes('VM')) {
          products.push('VM');
          features.push('Core');
        }
        setSelectedProducts(products);
        setSelectedFeatures(features);
      }
    }
  }, [selectedPlan, includeEvents, includeGO, includeVM, filterMode]);
  // Handle VM mode - auto-select VM product and Core feature
  useEffect(() => {
    if (filterMode === 'vm') {
      setSelectedProducts(['VM']);
      setSelectedFeatures(['Core']);
    }
  }, [filterMode]);
  const handleEditionSelect = (editionName: string) => {
    setSelectedEdition(editionName);
  };
  const handlePlanSelect = (planName: string) => {
    setSelectedPlan(planName);
  };
  const handleToggleGO = () => {
    setIncludeGO(!includeGO);
  };
  const handleToggleVM = () => {
    setIncludeVM(!includeVM);
  };
  const handleToggleEvents = () => {
    setIncludeEvents(!includeEvents);
  };
  const handleTabChange = (mode: FilterMode) => {
    setFilterMode(mode);
    if (mode === 'product-feature') {
      setSelectedEdition(null);
      setSelectedPlan(null);
      setIncludeGO(false);
      setIncludeVM(false);
      setIncludeEvents(false);
      setSelectedProducts([]);
      setSelectedFeatures([]);
    } else if (mode === 'edition') {
      setSelectedPlan(null);
      setIncludeEvents(false);
      setSelectedProducts([]);
      setSelectedFeatures([]);
    } else if (mode === 'plan') {
      setSelectedEdition(null);
      setSelectedProducts([]);
      setSelectedFeatures([]);
    } else if (mode === 'vm') {
      setSelectedEdition(null);
      setSelectedPlan(null);
      setIncludeGO(false);
      setIncludeVM(false);
      setIncludeEvents(false);
    }
  };
  const filteredFields = getFilteredFields(selectedProducts, selectedFeatures);
  const {
    vmFields,
    otherFields
  } = getFieldsByProduct(selectedProducts, selectedFeatures);
  // For VM mode, we don't need tabs in the table
  const showTabs = filterMode !== 'vm' && selectedProducts.includes('VM') && selectedProducts.some(p => p !== 'VM');
  // Check if GO is selected
  const hasGO = selectedProducts.includes('GO');
  // Check if Wealth is selected
  const hasWealth = selectedProducts.includes('Wealth');
  // Check if Smart Ask Amounts is selected
  const hasSmartAskAmounts = selectedFeatures.includes('Smart Ask Amounts');
  const handleClearAll = () => {
    if (filterMode === 'vm') {
      // In VM mode, don't clear - just stay on VM
      return;
    }
    setSelectedProducts([]);
    setSelectedFeatures([]);
    setSelectedEdition(null);
    setSelectedPlan(null);
    setIncludeGO(false);
    setIncludeVM(false);
    setIncludeEvents(false);
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-314">
      {/* Header */}
      <div className="bg-white border-b border-gray-200" data-id="element-315">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6" data-id="element-316">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900" data-id="element-317">
            Data Requirements Interactive Resource
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-318">
            Select products and features to view required and recommended fields
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8" data-id="element-319">
        {/* Tab Navigation - Scrollable on mobile */}
        <div className="bg-white rounded-t-lg border border-b-0 border-gray-200" data-id="element-320">
          <div className="flex overflow-x-auto border-b border-gray-200 -mb-px scrollbar-hide" data-id="element-321">
            <button onClick={() => handleTabChange('product-feature')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${filterMode === 'product-feature' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-322">
              Product/Feature
            </button>
            <button onClick={() => handleTabChange('edition')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${filterMode === 'edition' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-323">
              Edition
            </button>
            <button onClick={() => handleTabChange('plan')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${filterMode === 'plan' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-324">
              <span className="hidden sm:inline" data-id="element-325">Plan (old pricing)</span>
              <span className="sm:hidden" data-id="element-326">Plan</span>
            </button>
            <button onClick={() => handleTabChange('vm')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${filterMode === 'vm' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-327">
              <span className="hidden sm:inline" data-id="element-328">Volunteer Management</span>
              <span className="sm:hidden" data-id="element-329">VM</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-b-lg border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6" data-id="element-330">
          {filterMode === 'product-feature' ? <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-331">
                <div data-id="element-332">
                  <MultiSelect label="Products" options={productOptions} selectedValues={selectedProducts} onChange={setSelectedProducts} placeholder="Select products..." grouped={true} data-id="element-333" />
                  <p className="mt-2 text-xs text-gray-500" data-id="element-334">
                    Selecting a product will select all its data-powered
                    features by default
                  </p>
                  {hasGO && <p className="mt-1 text-xs text-gray-500" data-id="element-335">
                      GO's data needs are foundational, not feature specific, so
                      no GO specific features are listed
                    </p>}
                  {hasWealth && <p className="mt-1 text-xs text-gray-500" data-id="element-336">
                      Wealth's data needs are foundational, not feature
                      specific, so no Wealth specific features are listed
                    </p>}
                </div>

                <MultiSelect label="Features" options={availableFeatures} selectedValues={selectedFeatures} onChange={setSelectedFeatures} placeholder="Select features (optional)..." disabled={selectedProducts.length === 0} data-id="element-337" />
              </div>

              {/* Smart Ask Amounts Message */}
              {hasSmartAskAmounts && <SmartAskAmountsInfo data-id="element-338" />}
            </> : filterMode === 'edition' ? <EditionSelector selectedEdition={selectedEdition} onSelectEdition={handleEditionSelect} includeGO={includeGO} includeVM={includeVM} onToggleGO={handleToggleGO} onToggleVM={handleToggleVM} data-id="element-339" /> : filterMode === 'plan' ? <PlanSelector selectedPlan={selectedPlan} onSelectPlan={handlePlanSelect} includeEvents={includeEvents} includeVM={includeVM} includeGO={includeGO} onToggleEvents={handleToggleEvents} onToggleVM={handleToggleVM} onToggleGO={handleToggleGO} data-id="element-340" /> :
        // VM Mode Content
        <div className="space-y-4" data-id="element-341">
              <div data-id="element-342">
                <h3 className="text-base font-semibold text-gray-900 mb-2" data-id="element-343">
                  Volunteer Management Field Requirements
                </h3>
                <p className="text-sm text-gray-500" data-id="element-344">
                  Volunteer Management (VM) requires a separate data upload from
                  other products. The fields below show all required and
                  recommended data for VM functionality.
                </p>
              </div>

              <div className="bg-[#EFF6FF] border border-[#0047AB]/20 rounded-lg p-4" data-id="element-345">
                <div className="flex items-start gap-3" data-id="element-346">
                  <div className="flex-shrink-0 w-5 h-5 bg-[#0047AB] rounded-full flex items-center justify-center mt-0.5" data-id="element-347">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-348">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-349" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-350">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-351">
                      Separate Upload Required
                    </h4>
                    <p className="text-sm text-gray-700" data-id="element-352">
                      VM data is uploaded separately from other product data.
                      Review the fields below to ensure your VM data file
                      includes all required and recommended information.
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {filterMode !== 'vm' && (selectedProducts.length > 0 || selectedFeatures.length > 0) && <div className="flex justify-end mt-4" data-id="element-353">
                <button onClick={handleClearAll} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0047AB]" data-id="element-354">
                  Clear All
                </button>
              </div>}
        </div>

        {/* Results */}
        <FieldsTable fields={filteredFields} vmFields={vmFields} otherFields={otherFields} selectedProducts={selectedProducts} selectedFeatures={selectedFeatures} availableFeatures={availableFeatures} showTabs={showTabs} data-id="element-355" />
      </div>
    </div>;
}